import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// Keeps page/limit in the URL (?page=3&limit=20) instead of plain component
// state, so a refresh (or a shared/bookmarked link) lands back on the same
// page instead of resetting to page 1. Changing the limit resets to page 1,
// since the old page number usually no longer means the same rows.
//
// `key` namespaces the params for pages that show more than one paginated
// list at once (e.g. PartnerAnalytics' quality/readers tabs) — pass
// e.g. 'quality' and 'readers' so they don't collide in the URL.
export default function usePagination(defaultLimit = 20, key = '') {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageKey = key ? `${key}_page` : 'page';
  const limitKey = key ? `${key}_limit` : 'limit';

  const page = parseInt(searchParams.get(pageKey), 10) || 1;
  const limit = parseInt(searchParams.get(limitKey), 10) || defaultLimit;

  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const setPage = useCallback((newPage) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.set(pageKey, String(newPage));
      return next;
    });
  }, [pageKey]);

  const setLimit = useCallback((newLimit) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.set(limitKey, String(newLimit));
      next.set(pageKey, '1');
      return next;
    });
  }, [limitKey, pageKey]);

  return { page, limit, setPage, setLimit };
}
